/* eslint-env browser */
import { useCallback } from 'react';

import type { NoharaRepresentative } from '../../types/company.types';
import type { SaleStaff } from '../../types/table/company.types';

interface UseRepresentativeManagementProps {
  representatives: NoharaRepresentative[];
  updateForm: (data: Record<string, unknown>) => void;
  touchedFields: Set<string>;
  setTouchedFields: (fields: Set<string>) => void;
  maxRepresentatives?: number;
}

/**
 * Common hook for managing representatives (Nohara representatives)
 * Provides add, update, and remove functionality with touched field tracking
 */
export const useRepresentativeManagement = ({
  representatives,
  updateForm,
  touchedFields,
  setTouchedFields,
  maxRepresentatives = 10,
}: UseRepresentativeManagementProps) => {
  const addRepresentative = useCallback(() => {
    if (representatives.length < maxRepresentatives) {
      // Generate unique ID using UUID to ensure uniqueness and security
      const uniqueId = `rep-${globalThis.crypto.randomUUID()}`;
      const newRepresentative: NoharaRepresentative = {
        id: uniqueId,
        department: '',
        salesPerson: {
          id: '',
          lastName: '',
          firstName: '',
          code: '',
        },
      };
      const newIndex = representatives.length;

      updateForm({
        representatives: [...representatives, newRepresentative],
      });

      // Mark new representative fields as touched to trigger validation immediately
      const newTouched = new Set(touchedFields);
      newTouched.add(`representatives.${newIndex}.department`);
      newTouched.add(`representatives.${newIndex}.salesPerson`);
      setTouchedFields(newTouched);
    }
  }, [representatives, updateForm, touchedFields, setTouchedFields, maxRepresentatives]);

  const updateRepresentative = useCallback(
    (id: string, field: keyof NoharaRepresentative, value: string | SaleStaff) => {
      const repIndex = Number.parseInt(id);
      if (Number.isNaN(repIndex) || repIndex < 0 || repIndex >= representatives.length) return;

      updateForm({
        representatives: representatives.map((rep, i) =>
          i === repIndex ? { ...rep, [field]: value } : rep
        ),
      });

      // Mark representative field as touched
      setTouchedFields(new Set(touchedFields).add(`representatives.${repIndex}.${field}`));
    },
    [updateForm, representatives, setTouchedFields, touchedFields]
  );

  const removeRepresentative = useCallback(
    (id: string) => {
      const repIndex = Number.parseInt(id);
      if (Number.isNaN(repIndex) || repIndex < 0 || repIndex >= representatives.length) return;

      updateForm({
        representatives: representatives.filter((_, i) => i !== repIndex),
      });

      // Clean up touched fields for removed representative and re-index remaining ones
      const newTouched = new Set<string>();
      for (const field of touchedFields) {
        // Skip fields for removed representative
        if (
          field === `representatives.${repIndex}.department` ||
          field === `representatives.${repIndex}.salesPerson`
        ) {
          continue;
        }
        // Re-index fields after removed representative
        const match = /^representatives\.(\d+)\.(.+)$/.exec(field);
        if (match) {
          const fieldIndex = Number.parseInt(match[1]);
          const fieldName = match[2];
          if (fieldIndex > repIndex) {
            // Decrement index for fields after removed one
            newTouched.add(`representatives.${fieldIndex - 1}.${fieldName}`);
          } else {
            newTouched.add(field);
          }
        } else {
          newTouched.add(field);
        }
      }
      setTouchedFields(newTouched);
    },
    [updateForm, representatives, touchedFields, setTouchedFields]
  );

  return {
    addRepresentative,
    updateRepresentative,
    removeRepresentative,
    canAddRepresentative: representatives.length < maxRepresentatives,
  };
};
